import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { fmtEUR } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Plus, Heart } from "lucide-react";
import { toast } from "sonner";
import { useT } from "@/context/I18nContext";

export default function Shop() {
  const { t } = useT();
  const CATEGORIES = [
    { slug: "", label: t("shop.cat.all") },
    { slug: "protein-bars", label: t("shop.cat.protein_bars") },
    { slug: "cereal-bars", label: t("shop.cat.cereal_bars") },
    { slug: "granola", label: t("shop.cat.granola") },
    { slug: "sweets", label: t("shop.cat.sweets") },
    { slug: "bundles", label: t("shop.cat.bundles") },
  ];
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "";
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wishlist, setWishlist] = useState(new Set());
  const { add } = useCart();
  const { user } = useAuth();

  useEffect(() => {
    setLoading(true);
    api.get("/products", { params: category ? { category } : {} })
      .then((r) => setProducts(r.data))
      .finally(() => setLoading(false));
  }, [category]);

  useEffect(() => {
    if (!user) { setWishlist(new Set()); return; }
    api.get("/wishlist/me").then((r) => setWishlist(new Set(r.data.map((p) => p.id)))).catch(() => {});
  }, [user]);

  const addBoxOf12 = async (p) => {
    try {
      const { data } = await api.get(`/products/${p.slug}-box-of-12`);
      add(data, 1);
      toast.success(t("shop.box_added", { name: p.name }));
    } catch { toast.error("Box variant unavailable"); }
  };

  const toggleWishlist = async (p) => {
    if (!user) { toast.info(t("shop.sign_in_fav")); return; }
    try {
      const { data } = await api.post(`/wishlist/${p.id}`);
      setWishlist((prev) => {
        const n = new Set(prev);
        data.in_wishlist ? n.add(p.id) : n.delete(p.id);
        return n;
      });
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-16" data-testid="shop-page">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--brunchi-forest)] mb-2">{t("shop.eyebrow")}</div>
        <h1 className="font-display text-4xl lg:text-5xl">{t("shop.title")}</h1>
      </div>

      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2 mb-8" data-testid="category-tabs">
        {CATEGORIES.map((c) => (
          <button key={c.slug}
            onClick={() => setParams(c.slug ? { category: c.slug } : {})}
            data-testid={`category-${c.slug || "all"}`}
            className={`px-4 py-2 rounded-full text-sm whitespace-nowrap border transition ${
              category === c.slug
                ? "bg-[color:var(--brunchi-ink)] text-white border-[color:var(--brunchi-ink)]"
                : "bg-white border-[color:var(--brunchi-stone)] hover:border-[color:var(--brunchi-ink)]"
            }`}>
            {c.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-24 text-[color:var(--brunchi-ink-soft)]">{t("shop.loading")}</div>
      ) : products.length === 0 ? (
        <div className="text-center py-24">
          <div className="font-serif text-2xl mb-3">{t("shop.empty_title")}</div>
          <p className="text-sm text-[color:var(--brunchi-ink-soft)]">
            {t("shop.empty_body")}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6" data-testid="product-grid">
          {products.map((p) => (
            <div key={p.id} className="group bg-white rounded-xl overflow-hidden border border-[color:var(--brunchi-stone)] flex flex-col relative" data-testid={`product-card-${p.slug}`}>
              <button onClick={() => toggleWishlist(p)}
                data-testid={`wishlist-toggle-${p.slug}`}
                className="absolute top-2 right-2 z-10 w-8 h-8 rounded-full bg-white/90 backdrop-blur flex items-center justify-center hover:scale-110 transition"
                aria-label="Toggle wishlist">
                <Heart size={16}
                  fill={wishlist.has(p.id) ? "#8C3B1D" : "none"}
                  stroke={wishlist.has(p.id) ? "#8C3B1D" : "#3D3A34"} />
              </button>
              <Link to={`/shop/${p.slug}`} className="block aspect-square overflow-hidden bg-[color:var(--brunchi-cream-2)]">
                {p.images?.[0] ? (
                  <img src={p.images[0].startsWith("/api/media") ? `${p.images[0]}&w=480` : p.images[0]}
                    alt={p.name}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[color:var(--brunchi-stone-2)] font-serif text-4xl">
                    Brunchi
                  </div>
                )}
              </Link>
              <div className="p-4 flex flex-col flex-1">
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {(p.tags || []).slice(0, 2).map((t) => (
                    <span key={t} className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[color:var(--brunchi-cream-2)] text-[color:var(--brunchi-forest)]">
                      {t}
                    </span>
                  ))}
                </div>
                <Link to={`/shop/${p.slug}`} className="font-serif text-base sm:text-lg leading-tight hover:text-[color:var(--brunchi-terracotta)]">
                  {p.name}
                </Link>
                <div className="mt-1 text-xs text-[color:var(--brunchi-ink-soft)] line-clamp-2">{p.description}</div>
                <div className="mt-3 flex items-center justify-between">
                  <div>
                    <div className="font-mono text-base font-semibold text-[color:var(--brunchi-terracotta)]">
                      {fmtEUR(p.price_eur)}
                    </div>
                  </div>
                  <button onClick={() => add(p, 1)}
                    data-testid={`add-to-cart-${p.slug}`}
                    className="w-9 h-9 rounded-full pill-primary flex items-center justify-center"
                    aria-label={t("shop.add_to_cart")}>
                    <Plus size={16} />
                  </button>
                </div>
                {p.box_of_12_eur && (
                  <button onClick={() => addBoxOf12(p)}
                    data-testid={`add-box-${p.slug}`}
                    className="mt-2 w-full py-2 rounded-full pill-outline text-xs font-medium">
                    {t("shop.box_of_12", { price: fmtEUR(p.box_of_12_eur) })}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
